import shareService from '../services/shareService';
import { prepareRequestBodyForApi } from './requestPayload';
import { loadWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { redactSnapshotPayload } from './shareRedaction';
import { ENC_ALGORITHM, isEncryptedPayload } from './shareEnvelope';

/**
 * Chat-scoped sharing: helpers to mint a recipient-scoped share link and build
 * the `kind: 'share'` message metadata the chat renders as a share card.
 *
 * Metadata wire format (v1):
 *   {
 *     share_type: 'workspace' | 'collection' | 'request',
 *     token,            // scoped share_links token
 *     label,            // display name
 *     method?,          // requests: method badge
 *     item_count?,      // collection: request count; workspace: collection count
 *     workspace_name?,  // context sub-line
 *     snapshot?: true,  // payload-backed share of a local-only resource
 *     enc?: 'aes-gcm-256',  // the snapshot payload is an encrypted envelope
 *     resource: { type, id },  // origin ids — reserved for the later
 *                              // "open in builder" (interpretation) phase
 *     v: 1,
 *   }
 */

// Server caps snapshot payloads at 2 MB of JSON.
export const MAX_SNAPSHOT_BYTES = 2_000_000;

// Server caps message metadata at 16,000 JSON bytes — clip every free-text
// string that goes in (unnamed requests fall back to their URL, which can be
// arbitrarily long). Worst case: 30 preview rows × ~130 B ≈ 4 KB.
const clip = (s, n) => (typeof s === 'string' && s.length > n ? s.slice(0, n) : s);

export function buildShareMetadata({ type, item, workspaceName = null, snapshot = false }) {
  const meta = {
    share_type: type,
    label: clip(item?.name || 'Untitled', 200),
    resource: { type, id: snapshot ? null : item?.id ?? null },
    v: 1,
  };
  if (type === 'request') meta.method = (item?.method || 'GET').toUpperCase();
  if (type === 'collection') {
    meta.item_count = (item?.requests || []).length;
    // Lightweight preview so the chat card can render the collection's
    // requests the way the sidebar does. Capped — the card is a preview, the
    // fork delivers the real thing.
    meta.requests = (item?.requests || []).slice(0, 30).map((r) => ({
      name: clip(r?.name || r?.url || 'Untitled', 120),
      method: (r?.method || 'GET').toUpperCase(),
    }));
  }
  if (type === 'workspace' && Array.isArray(item?.collections)) {
    meta.item_count = item.collections.length;
  }
  if (workspaceName) meta.workspace_name = clip(workspaceName, 200);
  if (snapshot) meta.snapshot = true;
  return meta;
}

/**
 * Mint a share link scoped to `channelId`. Chat shares never expire and have
 * no use cap — recipient scoping (channel membership, checked server-side on
 * every access) is the guard, and a channel with N members needs N accepts.
 */
export async function createShareForChat(channelId, { type, id = null, payload = null }) {
  return shareService.createShareLink(type, payload ? null : id, {
    expiresInDays: null,
    maxUses: null,
    chatChannelId: channelId,
    payload,
  });
}

/**
 * One-call flow used by both share surfaces (composer picker and the sidebar
 * "Share → In Chat" modal): create the scoped link, return the message
 * metadata ready for `onSend('', 'share', metadata)` / chatService.sendMessage.
 * Pass `payload` (from buildSnapshotPayload) for local-only resources.
 */
export async function shareToChannel(channelId, { type, item, workspaceName = null, payload = null }) {
  const share = await createShareForChat(channelId, { type, id: item?.id, payload });
  const metadata = buildShareMetadata({
    type,
    item,
    workspaceName,
    snapshot: Boolean(payload),
  });
  metadata.token = share.token;
  // Read off the payload that was actually uploaded rather than taken as an
  // argument, so the card's `enc` flag CANNOT disagree with what is in the
  // share row. Two effects, both wanted: the accepting client knows to decrypt
  // without a speculative fetch, and `ChatMessageView`'s existing
  // `canForward = !metadata.enc` rule stops offering Forward on a card whose
  // ciphertext is keyed to THIS channel and would be undecryptable in another.
  if (isEncryptedPayload(payload)) metadata.enc = ENC_ALGORITHM;
  return metadata;
}

// ── Snapshot serialization (local / git workspaces have no server rows) ──────

function serializeRequestForSnapshot(request) {
  const body = prepareRequestBodyForApi(request, null, request?.name || 'Untitled Request');
  delete body.collection_id;
  delete body.folder_id;
  delete body.mock;
  return body;
}

function serializeCollectionForSnapshot(collection) {
  return {
    name: collection?.name || 'Untitled Collection',
    description: collection?.description || '',
    settings: collection?.settings || {},
    sort_order: collection?.sort_order ?? 0,
    requests: (collection?.requests || []).map((r) => serializeRequestForSnapshot(r)),
  };
}

/**
 * Build the `payload` for a snapshot share. Shape matches what the backend
 * accepts: top-level name/description for the share-info card, plus the data
 * under a key named after the share type.
 *
 * Credentials are REDACTED unless `includeCredentials` is passed. The payload is
 * uploaded to our API as plaintext JSON, stored there unencrypted and unexpiring,
 * and delivered to everyone in the channel — so sharing working auth has to be a
 * decision somebody made on purpose, not something that happens because they
 * shared a collection.
 *
 * Redaction is ONE PASS OVER THE FINISHED PAYLOAD (utils/shareRedaction.js), not
 * per-field work inside the serializers above. That is deliberate and it is the
 * whole point: patching named fields in the serializer fails open on every field
 * added later, and it already did — the common `auth: { type: 'inherit' }`
 * request keeps its credential in `collection.settings.collection_auth`, which no
 * per-request redactor ever sees. Anything new that `prepareRequestBodyForApi` or
 * `stripFolderIdForPayload` emits is covered by this single call.
 *
 * What survives redaction, on purpose: every key NAME (headers, params, cookies,
 * variables), `auth.type`, and the structure — a share stripped to nothing is not
 * reproducible. What redaction cannot cover: request bodies and scripts; see
 * `findUnredactableRisks`, which names them for the user instead.
 */
export function buildSnapshotPayload(type, item, { includeCredentials = false } = {}) {
  const payload = buildRawSnapshotPayload(type, item);
  return includeCredentials ? payload : redactSnapshotPayload(payload);
}

function buildRawSnapshotPayload(type, item) {
  if (type === 'request') {
    return {
      name: item?.name || 'Untitled Request',
      description: item?.description || '',
      request: serializeRequestForSnapshot(item),
    };
  }
  if (type === 'collection') {
    return {
      name: item?.name || 'Untitled Collection',
      description: item?.description || '',
      collection: serializeCollectionForSnapshot(item),
    };
  }
  if (type === 'workspace') {
    return {
      name: item?.name || 'Untitled Workspace',
      description: item?.description || '',
      workspace: {
        name: item?.name || 'Untitled Workspace',
        description: item?.description || '',
        settings: workspaceSnapshotSettings(item),
        collections: (item?.collections || []).map((c) => serializeCollectionForSnapshot(c)),
      },
    };
  }
  throw new Error(`Unsupported snapshot share type: ${type}`);
}

// Local snapshots keep variables in dedicated fields (globalVariables /
// workspaceVariables / environments — see localWorkspaceStore.normalizeSnapshot),
// NOT in `settings`. Map them to the settings keys the server merges into the
// forked workspace (mirrors buildPublishLocalPayload) or the fork arrives with
// every {{placeholder}} broken.
function workspaceSnapshotSettings(item) {
  const settings = { ...(item?.settings || {}) };
  if (Array.isArray(item?.globalVariables)) settings.globals = item.globalVariables;
  if (Array.isArray(item?.workspaceVariables)) settings.workspace_globals = item.workspaceVariables;
  if (Array.isArray(item?.environments)) settings.environments = item.environments;
  return settings;
}

/**
 * Hydrate a local/git workspace item with everything a workspace snapshot
 * share needs: the collection tree AND the variable fields. Pass
 * `collections` to keep an already-loaded (possibly fresher) tree; otherwise
 * the item's own tree or the stored snapshot's is used. Pass `snap` (an
 * already-loaded snapshot) to avoid a second store read. Loads go through
 * workspaceStoreRouter so git workspaces (SQLite/YAML) hydrate too — the
 * legacy local-JSON store only holds `storage: 'local'` workspaces.
 */
export async function hydrateWorkspaceSnapshotItem(item, collections = null, snap = null) {
  if (!snap) snap = await loadWorkspaceSnapshot(item);
  return {
    ...item,
    collections: Array.isArray(collections)
      ? collections
      : Array.isArray(item?.collections)
        ? item.collections
        : snap?.collections || [],
    globalVariables: snap?.globalVariables || [],
    workspaceVariables: snap?.workspaceVariables || [],
    environments: snap?.environments || [],
  };
}

/** Throws a user-facing error when the snapshot exceeds the server cap. */
export function assertSnapshotSize(payload) {
  const bytes = new Blob([JSON.stringify(payload)]).size;
  if (bytes > MAX_SNAPSHOT_BYTES) {
    throw new Error('This item is too large to share as a snapshot (max 2 MB)');
  }
  return payload;
}
